import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Search, Paperclip } from "lucide-react";
import { useFetch } from "../../lib/useFetch";
import { useSeo } from "../../lib/hooks";
import { fmtDate, API, fmtSize } from "../../lib/api";
import { PageHero } from "../../components/site/SiteLayout";
import { TeamCard, inputCls } from "../../components/site/ui";

export function Studio() {
  useSeo("Lo studio", "Lo studio commercialista Diolosa a Spilimbergo: professionisti, collaboratori e metodo di lavoro.");
  const team = useFetch("/public/team").data;
  const pros = (team || []).filter((t) => t.kind === "professional");
  const collabs = (team || []).filter((t) => t.kind === "collaborator");
  return (
    <>
      <PageHero eyebrow="Lo studio" title="Un riferimento a Spilimbergo per imprese, professionisti e privati">
        Lo studio affianca i propri clienti negli adempimenti fiscali, contabili e societari con un rapporto diretto e un'area riservata per lo scambio sicuro dei documenti.
      </PageHero>
      <section className="section container-x grid gap-10 lg:grid-cols-3">
        {[["Ascolto", "Ogni incarico parte dalla comprensione della situazione del cliente e delle sue esigenze."],
          ["Organizzazione", "Scadenze, richieste di documenti e comunicazioni sono gestite in modo ordinato e tracciabile."],
          ["Riservatezza", "I documenti si scambiano tramite l'area clienti protetta, non tramite moduli pubblici o email."]].map(([t, d]) => (
          <div key={t} className="border-l-2 border-[hsl(var(--accent))] pl-5"><h2 className="text-2xl font-semibold">{t}</h2><p className="mt-2 text-muted-foreground">{d}</p></div>
        ))}
      </section>
      <section className="section bg-secondary/50 border-y" aria-labelledby="pro-title">
        <div className="container-x">
          <p className="eyebrow">Team</p>
          <h2 id="pro-title" className="text-2xl sm:text-3xl lg:text-4xl font-semibold mt-2 mb-10">Professionisti</h2>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3" data-testid="professionals-grid">{pros.map((t) => <TeamCard key={t.id} t={t} />)}</div>
        </div>
      </section>
      {collabs.length > 0 && (
        <section className="section container-x" aria-labelledby="collab-title" data-testid="collaborators-section">
          <h2 id="collab-title" className="text-2xl sm:text-3xl lg:text-4xl font-semibold mb-10">Collaboratori</h2>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">{collabs.map((t) => <TeamCard key={t.id} t={t} />)}</div>
        </section>
      )}
    </>
  );
}

export function Servizi() {
  useSeo("Servizi", "Consulenza fiscale, contabilità, dichiarazioni, bilanci, apertura partita IVA e controllo di gestione a Spilimbergo e in provincia di Pordenone.");
  const services = useFetch("/public/services").data || [];
  return (
    <>
      <PageHero eyebrow="Servizi" title="Servizi per imprese, professionisti e privati" />
      <section className="section container-x">
        <ol className="divide-y border-y">
          {services.map((s, i) => (
            <li key={s.id} id={`servizio-${i + 1}`} className="py-8 grid md:grid-cols-12 gap-4" data-testid={`service-${i}`}>
              <span className="md:col-span-1 font-mono-ui text-sm text-muted-foreground">{String(i + 1).padStart(2, "0")}</span>
              <h2 className="md:col-span-4 text-2xl font-semibold">{s.title}</h2>
              <div className="md:col-span-7 text-muted-foreground leading-relaxed whitespace-pre-line">{s.summary}{s.body && <p className="mt-3">{s.body}</p>}</div>
            </li>
          ))}
        </ol>
        <p className="mt-10"><Link className="underline font-semibold" to="/appuntamento">Richiedi un appuntamento</Link> per approfondire le tue esigenze.</p>
      </section>
    </>
  );
}

export function News() {
  useSeo("News e circolari", "Circolari informative e notizie dello Studio Diolosa – Commercialisti di Spilimbergo.");
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [query, setQuery] = useState("");
  const { data } = useFetch(`/public/news?limit=50&q=${encodeURIComponent(query)}&category=${encodeURIComponent(cat)}`);
  return (
    <>
      <PageHero eyebrow="Informazione" title="News e circolari">Informazioni di carattere generale. Le comunicazioni riservate a un singolo cliente sono disponibili solo nell'area clienti.</PageHero>
      <section className="section container-x">
        <form role="search" className="flex flex-col sm:flex-row gap-3 mb-8" onSubmit={(e) => { e.preventDefault(); setQuery(q); }}>
          <label htmlFor="news-q" className="sr-only">Cerca</label>
          <input id="news-q" className={inputCls} placeholder="Cerca negli articoli" value={q} onChange={(e) => setQ(e.target.value)} data-testid="news-search-input" />
          <label htmlFor="news-cat" className="sr-only">Categoria</label>
          <select id="news-cat" className={`${inputCls} sm:w-56`} value={cat} onChange={(e) => setCat(e.target.value)} data-testid="news-category-select">
            <option value="">Tutte le categorie</option>{(data?.categories || []).map((c) => <option key={c}>{c}</option>)}
          </select>
          <button className="tap px-5 rounded-md bg-primary text-primary-foreground font-semibold inline-flex items-center gap-2 justify-center" data-testid="news-search-btn"><Search className="h-4 w-4" aria-hidden />Cerca</button>
        </form>
        <p className="text-sm text-muted-foreground mb-4" aria-live="polite">{data ? `${data.total} risultati` : "Caricamento…"}</p>
        <ul className="divide-y border-y">
          {(data?.items || []).map((n) => (
            <li key={n.id} className="py-6"><Link to={`/news/${n.slug}`} className="group block" data-testid={`news-item-${n.slug}`}>
              <p className="text-xs"><span className="eyebrow mr-2">{n.category}</span><time dateTime={n.published_at}>{fmtDate(n.published_at)}</time></p>
              <h2 className="text-xl sm:text-2xl font-semibold mt-2 group-hover:underline underline-offset-4 break-words">{n.title}</h2>
              <p className="text-muted-foreground mt-1">{n.excerpt}</p>
            </Link></li>
          ))}
        </ul>
      </section>
    </>
  );
}

export function NewsDetail() {
  const { slug } = useParams();
  const { data: n, error } = useFetch(`/public/news/${slug}`);
  useSeo(n?.title || "Articolo", n?.excerpt);
  if (error) return <div className="container-x section"><p role="alert">{error}</p></div>;
  if (!n) return <div className="container-x section">Caricamento…</div>;
  return (
    <article className="container-x section max-w-3xl">
      <Link to="/news" className="text-sm underline">← Tutte le news</Link>
      <p className="mt-6 text-xs"><span className="eyebrow mr-2">{n.category}</span><time dateTime={n.published_at}>{fmtDate(n.published_at)}</time></p>
      <h1 className="text-4xl sm:text-5xl font-semibold mt-3 break-words">{n.title}</h1>
      <p className="mt-2 text-xs text-muted-foreground">Informazione di carattere generale: non sostituisce una consulenza specifica.</p>
      <div className="prose-text mt-8 text-base sm:text-lg">{(n.body || "").split("\n\n").map((p, i) => <p key={i} className="whitespace-pre-line">{p}</p>)}</div>
      {n.attachments?.length > 0 && <div className="mt-10 card-line p-5"><h2 className="text-lg font-semibold mb-3">Allegati</h2><ul className="space-y-2">
        {n.attachments.map((a) => <li key={a.file_id}><a className="tap inline-flex items-center gap-2 underline" href={`${API}/public/files/${a.file_id}`} data-testid="news-attachment"><Paperclip className="h-4 w-4" aria-hidden />{a.name} <span className="text-xs text-muted-foreground">({fmtSize(a.size)})</span></a></li>)}
      </ul></div>}
    </article>
  );
}
